import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Observable, of, from } from 'rxjs';
import { map, switchMap, catchError, mergeMap, toArray, finalize } from 'rxjs/operators';
import { BACKEND_URL } from '../app.config';

import { KeywordFieldReferences, QuestionnaireUsageResult } from '../api/data-model';
import { QuestionnaireTemplateService } from './questionnaire.template.service';
import { FormDesignService } from './form-design.service';
import { EntityType } from '../api/enum/entity-type';

export type SyncProgressCallback = (status: string) => void;

@Injectable()
export class KeywordService {
  constructor(
    private readonly http: HttpClient,
    @Inject(BACKEND_URL) private readonly baseUrl: string,
    private readonly questionnaireTemplateService: QuestionnaireTemplateService,
    private readonly formDesignService: FormDesignService
  ) {}

   exportAll(): Observable<Blob> {
    return this.http.get(this.baseUrl + '/api/app/keyword/export-all', {
      responseType: 'blob',
    });
  }

  getReferences(keyword: string): Observable<KeywordFieldReferences> {
    return this.http.get<KeywordFieldReferences>(`${this.baseUrl}/api/app/keyword/references`, {
      params: { keyword }
    });
  }

  /**
   * Get questionnaires that use a specific keyword
   * @param keyword - The keyword to check usage for
   * @param onProgress - Optional callback to report progress
   * @returns Observable with array of questionnaires using this keyword
   */
  getQuestionnaireUsage(
    keyword: string,
    onProgress?: SyncProgressCallback
  ): Observable<QuestionnaireUsageResult[]> {
    if (onProgress) {
      onProgress('Checking questionnaire usage...');
    }

    return this.questionnaireTemplateService.getList('', 0, 1000).pipe(
      switchMap((questionnaires) => {
        if (!questionnaires.items || questionnaires.items.length === 0) {
          return of([]);
        }

        const total = questionnaires.items.length;
        let completed = 0;
        const pattern = new RegExp(`"${keyword}"`);

        return from(questionnaires.items).pipe(
          mergeMap(
            (questionnaire) => {
              return this.formDesignService.query(EntityType.Questionnaire, questionnaire.id, false).pipe(
                map((formDesign) => {
                  if (formDesign && formDesign.data && pattern.test(formDesign.data)) {
                    return {
                      name: questionnaire.name,
                      audience: questionnaire.audience,
                      formDesign: formDesign
                    };
                  }

                  return null;
                }),
                catchError(() => {
                  return of(null);
                }),
                finalize(() => {
                  completed++;
                  if (onProgress && completed % 5 === 0) {
                    const percent = Math.round((completed / total) * 100);
                    onProgress(`Checking Questionnaires... ${completed}/${total} (${percent}%)`);
                  }
                })
              );
            },
            10 // Concurrency limit of 10
          ),
          toArray(),
          map((results) => results.filter((item): item is QuestionnaireUsageResult => item !== null))
        );
      })
    );
  }
}
